import { useEffect, useState, useMemo, useRef } from 'react';
import { 
  Search, 
  Filter, 
  Phone, 
  Mail, 
  MapPin,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Eye,
  Trash2,
  Check,
  Users,
  X
} from 'lucide-react';
import { useStore } from '../lib/store';
import { PIPELINE_STAGES, Lead, PipelineStage, getStageConfig, normalizeStatus } from '../lib/pocketbase';
import { formatDate, formatPhone, getInitials, cn } from '../lib/utils';
import LeadDetail from '../components/LeadDetail';

type SortField = 'name' | 'email' | 'activity' | 'location' | 'status' | 'created';
type SortDir = 'asc' | 'desc';

export default function Leads() {
  const { leads, selectedLead, selectLead, fetchLeads, updateLeadStatus, deleteLead, loading } = useStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PipelineStage | 'all'>('all');
  const [sortField, setSortField] = useState<SortField>('created');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    fetchLeads();
  }, []);
  
  // Fermer dropdown au clic extérieur
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Filtrage et tri
  const filteredLeads = useMemo(() => {
    let result = [...leads];
    
    // Recherche
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(lead => 
        lead.name?.toLowerCase().includes(q) ||
        lead.company?.toLowerCase().includes(q) ||
        lead.email?.toLowerCase().includes(q) ||
        lead.phone?.includes(q) ||
        lead.activity?.toLowerCase().includes(q) ||
        lead.location?.toLowerCase().includes(q)
      );
    }
    
    // Filtre status
    if (statusFilter !== 'all') {
      result = result.filter(lead => normalizeStatus(lead.status) === statusFilter);
    }
    
    // Tri
    result.sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      
      switch (sortField) {
        case 'name':
          aVal = (a.name || a.company || '').toLowerCase();
          bVal = (b.name || b.company || '').toLowerCase();
          break;
        case 'email':
          aVal = (a.email || '').toLowerCase();
          bVal = (b.email || '').toLowerCase();
          break;
        case 'activity':
          aVal = (a.activity || '').toLowerCase();
          bVal = (b.activity || '').toLowerCase();
          break;
        case 'location':
          aVal = (a.location || '').toLowerCase();
          bVal = (b.location || '').toLowerCase();
          break;
        case 'status':
          aVal = PIPELINE_STAGES.findIndex(s => s.id === normalizeStatus(a.status));
          bVal = PIPELINE_STAGES.findIndex(s => s.id === normalizeStatus(b.status));
          break;
        case 'created':
          aVal = new Date(a.created).getTime() || 0;
          bVal = new Date(b.created).getTime() || 0;
          break;
      }
      
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [leads, search, statusFilter, sortField, sortDir]);
  
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };
  
  const handleStatusChange = async (leadId: string, newStatus: PipelineStage) => {
    await updateLeadStatus(leadId, newStatus);
    setActiveDropdown(null);
  };
  
  const handleDelete = async (leadId: string) => {
    try {
      await deleteLead(leadId);
    } catch (err) {
      console.error('Failed to delete lead:', err);
      alert('Erreur lors de la suppression du lead.');
    }
    setDeleteConfirm(null);
    setActiveDropdown(null);
  };
  
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronDown className="w-4 h-4 text-gray-300" />;
    return sortDir === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-primary-500" /> 
      : <ChevronDown className="w-4 h-4 text-primary-500" />;
  };
  
  const StatusBadge = ({ status }: { status: string | undefined }) => {
    const config = getStageConfig(status);
    return (
      <span className={cn(
        'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium',
        config.bgLight,
        config.textColor
      )}>
        <span>{config.emoji}</span>
        {config.label}
      </span>
    );
  };
  
  // Stats rapides
  const stats = useMemo(() => {
    const total = leads.length;
    const byStatus = PIPELINE_STAGES.reduce((acc, stage) => {
      acc[stage.id] = leads.filter(l => normalizeStatus(l.status) === stage.id).length;
      return acc;
    }, {} as Record<PipelineStage, number>);
    return { total, byStatus };
  }, [leads]);
  
  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Leads</h1>
        <p className="text-gray-500 mt-1">Gérez vos prospects et suivez leur progression</p>
      </div>
      
      {/* Quick Stats */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <button
          onClick={() => setStatusFilter('all')}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all whitespace-nowrap',
            statusFilter === 'all' 
              ? 'bg-gray-900 text-white border-gray-900' 
              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
          )}
        >
          <Users className="w-4 h-4" />
          Tous
          <span className={cn(
            'px-2 py-0.5 rounded-full text-xs',
            statusFilter === 'all' ? 'bg-white/20' : 'bg-gray-100'
          )}>
            {stats.total}
          </span>
        </button>
        
        {PIPELINE_STAGES.map(stage => (
          <button
            key={stage.id}
            onClick={() => setStatusFilter(stage.id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all whitespace-nowrap',
              statusFilter === stage.id
                ? `${stage.bgLight} ${stage.textColor} ${stage.borderColor}`
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
            )}
          >
            <span>{stage.emoji}</span>
            {stage.label}
            <span className={cn(
              'px-2 py-0.5 rounded-full text-xs',
              statusFilter === stage.id ? `${stage.color} text-white` : 'bg-gray-100'
            )}>
              {stats.byStatus[stage.id] || 0}
            </span>
          </button>
        ))}
      </div>
      
      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par nom, email, téléphone, activité..."
            className="w-full h-10 pl-10 pr-4 bg-white border border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-shadow"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/80">
                <th 
                  onClick={() => handleSort('name')}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                >
                  <div className="flex items-center gap-2">
                    Lead
                    <SortIcon field="name" />
                  </div>
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th 
                  onClick={() => handleSort('activity')}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                >
                  <div className="flex items-center gap-2">
                    Activité
                    <SortIcon field="activity" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('location')}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none hidden lg:table-cell"
                >
                  <div className="flex items-center gap-2">
                    Localisation
                    <SortIcon field="location" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('status')}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                >
                  <div className="flex items-center gap-2">
                    Statut
                    <SortIcon field="status" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('created')}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none hidden md:table-cell"
                >
                  <div className="flex items-center gap-2">
                    Date
                    <SortIcon field="created" />
                  </div>
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                // Loading skeleton
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
                        <div className="space-y-2">
                          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                          <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4"><div className="h-4 w-20 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="px-4 py-4"><div className="h-4 w-28 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="px-4 py-4 hidden lg:table-cell"><div className="h-4 w-24 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="px-4 py-4"><div className="h-6 w-20 bg-gray-200 rounded-full animate-pulse" /></td>
                    <td className="px-4 py-4 hidden md:table-cell"><div className="h-4 w-28 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="px-4 py-4"><div className="h-8 w-8 bg-gray-200 rounded animate-pulse ml-auto" /></td>
                  </tr>
                ))
              ) : filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-16">
                    <div className="flex flex-col items-center justify-center text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <Users className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-900 font-medium mb-1">Aucun lead trouvé</p>
                      <p className="text-sm text-gray-500">
                        {search ? 'Essayez avec d\'autres termes de recherche' : 'Les nouveaux leads apparaîtront ici'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredLeads.map(lead => (
                  <tr 
                    key={lead.id}
                    className="group hover:bg-gray-50/50 transition-colors"
                  >
                    {/* Lead info */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center text-primary-700 font-medium text-sm flex-shrink-0">
                          {getInitials(lead.name || lead.company)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {lead.name || lead.company || 'Sans nom'}
                          </p>
                          {lead.company && lead.name && (
                            <p className="text-sm text-gray-500 truncate">{lead.company}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    
                    {/* Contact */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {lead.email && (
                          <a 
                            href={`mailto:${lead.email}`}
                            className="p-2 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                            title={lead.email}
                          >
                            <Mail className="w-4 h-4" />
                          </a>
                        )}
                        {lead.phone && (
                          <a 
                            href={`tel:${lead.phone}`}
                            className="p-2 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                            title={formatPhone(lead.phone)}
                          >
                            <Phone className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </td>
                    
                    {/* Activity */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 truncate block max-w-[200px]">
                        {lead.activity || '—'}
                      </span>
                    </td>
                    
                    {/* Location */}
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {lead.location ? (
                        <div className="flex items-center gap-1.5 text-sm text-gray-500">
                          <MapPin className="w-3.5 h-3.5" />
                          <span className="truncate max-w-[150px]">{lead.location}</span>
                        </div>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    
                    {/* Status */}
                    <td className="px-4 py-3">
                      <div className="relative" ref={activeDropdown === `status-${lead.id}` ? dropdownRef : null}>
                        <button
                          onClick={() => setActiveDropdown(activeDropdown === `status-${lead.id}` ? null : `status-${lead.id}`)}
                          className="hover:opacity-80 transition-opacity"
                        >
                          <StatusBadge status={lead.status} />
                        </button>
                        
                        {activeDropdown === `status-${lead.id}` && (
                          <div className="absolute top-full left-0 mt-1 py-1 bg-white rounded-lg border border-gray-200 shadow-lg z-20 min-w-[160px] animate-fadeIn">
                            {PIPELINE_STAGES.map(stage => (
                              <button
                                key={stage.id}
                                onClick={() => handleStatusChange(lead.id, stage.id)}
                                className={cn(
                                  'flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50 transition-colors',
                                  normalizeStatus(lead.status) === stage.id && 'bg-gray-50'
                                )}
                              >
                                <span>{stage.emoji}</span>
                                <span className="flex-1 text-left">{stage.label}</span>
                                {normalizeStatus(lead.status) === stage.id && (
                                  <Check className="w-4 h-4 text-primary-500" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    
                    {/* Date */}
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm text-gray-500">
                        {formatDate(lead.created, "d MMM yyyy")}
                      </span>
                    </td>
                    
                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => selectLead(lead)}
                          className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        <div className="relative" ref={activeDropdown === `actions-${lead.id}` ? dropdownRef : null}>
                          <button
                            onClick={() => setActiveDropdown(activeDropdown === `actions-${lead.id}` ? null : `actions-${lead.id}`)}
                            className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                          
                          {activeDropdown === `actions-${lead.id}` && (
                            <div className="absolute top-full right-0 mt-1 py-1 bg-white rounded-lg border border-gray-200 shadow-lg z-20 min-w-[180px] animate-fadeIn">
                              <button
                                onClick={() => { selectLead(lead); setActiveDropdown(null); }}
                                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                                Voir les détails
                              </button>
                              
                              {lead.email && (
                                <a
                                  href={`mailto:${lead.email}`}
                                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                  <Mail className="w-4 h-4" />
                                  Envoyer un email
                                </a>
                              )}
                              
                              {lead.phone && (
                                <a
                                  href={`tel:${lead.phone}`}
                                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                  <Phone className="w-4 h-4" />
                                  Appeler
                                </a>
                              )}
                              
                              <div className="h-px bg-gray-100 my-1" />
                              
                              <button
                                onClick={() => setDeleteConfirm(lead.id)}
                                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                                Supprimer
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Footer */}
        {filteredLeads.length > 0 && (
          <div className="px-4 py-3 bg-gray-50/50 border-t border-gray-100 text-sm text-gray-500">
            {filteredLeads.length} lead{filteredLeads.length > 1 ? 's' : ''} 
            {search || statusFilter !== 'all' ? ' (filtré)' : ''}
          </div>
        )}
      </div>
      
      {/* Lead Detail Panel */}
      {selectedLead && (
        <LeadDetail 
          lead={selectedLead} 
          onClose={() => selectLead(null)} 
        />
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-slideUp">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Supprimer ce lead ?</h3>
            <p className="text-gray-500 mb-6">
              Cette action est irréversible. Toutes les données associées seront perdues.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
